import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Source, SourceDetail, SourceInput } from '../../types';
import * as api from '../../services/api';

/**
 * /sources — the Source directory.
 *
 * A Source is the company/person/entity that sends leads INTO a data body:
 * whoever POSTs to its webhook URL. The directory is seeded implicitly — the
 * Build New Data Body screen creates a source the first time a name is typed
 * — and this page is where the rest of the record gets filled in.
 *
 * Clicking a row opens a detail modal with the full record (company, contact,
 * email, phone, address, notes), the data bodies assigned to that source, and
 * a link straight into its reporting drilldown.
 *
 * Account-isolated server-side: the API resolves the account from the JWT,
 * there is no caller-supplied tenant id.
 */

const EMPTY_INPUT: SourceInput = {
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  notes: '',
};

function toInput(s: Source): SourceInput {
  return {
    name: s.name,
    contactName: s.contactName ?? '',
    contactEmail: s.contactEmail ?? '',
    contactPhone: s.contactPhone ?? '',
    addressLine1: s.addressLine1 ?? '',
    addressLine2: s.addressLine2 ?? '',
    city: s.city ?? '',
    state: s.state ?? '',
    postalCode: s.postalCode ?? '',
    country: s.country ?? '',
    notes: s.notes ?? '',
  };
}

export default function SourcesPage() {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SourceDetail | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSources(await api.getSources());
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function openSource(id: string) {
    try {
      setSelected(await api.getSource(id));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load source');
    }
  }

  const q = search.trim().toLowerCase();
  const visible = q
    ? sources.filter(s =>
        [s.name, s.contactName, s.contactEmail, s.contactPhone, s.city, s.state]
          .some(v => (v || '').toLowerCase().includes(q)))
    : sources;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sources</h1>
          <p className="page-subtitle">
            The companies and people who send leads into your data bodies. One source can own several bodies.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>Add Source</button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 16, maxWidth: 360 }}>
        <input
          className="form-input"
          type="search"
          placeholder="Search sources…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading && <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>}

      {!loading && visible.length === 0 && (
        <div style={{ padding: 32, background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, color: '#6b7280', textAlign: 'center' }}>
          {sources.length === 0
            ? 'No sources yet. They get created automatically the first time you name one while building a data body — or add one here.'
            : 'No sources match that search.'}
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1.2fr 1.4fr 1fr 110px',
            gap: 12,
            padding: '10px 16px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            fontSize: 11,
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            <div>Company</div>
            <div>Contact</div>
            <div>Email</div>
            <div>Phone</div>
            <div style={{ textAlign: 'right' }}>Data Bodies</div>
          </div>
          {visible.map((s, i) => (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => openSource(s.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void openSource(s.id); } }}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.2fr 1.4fr 1fr 110px',
                gap: 12,
                padding: '13px 16px',
                borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                alignItems: 'center',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, color: '#111827' }}>{s.name}</div>
              <div style={{ color: s.contactName ? '#374151' : '#d1d5db' }}>{s.contactName || '—'}</div>
              <div style={{ color: s.contactEmail ? '#374151' : '#d1d5db', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.contactEmail || '—'}</div>
              <div style={{ color: s.contactPhone ? '#374151' : '#d1d5db' }}>{s.contactPhone || '—'}</div>
              <div style={{ textAlign: 'right', color: '#6b7280' }}>{s.bodyCount ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <SourceModal
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={async () => { setCreating(false); await load(); }}
        />
      )}

      {selected && (
        <SourceModal
          mode="edit"
          detail={selected}
          onClose={() => setSelected(null)}
          onSaved={async () => { setSelected(null); await load(); }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Detail / create modal
// -------------------------------------------------------------------
// One component for both because the fields are identical; `mode` only
// changes the title, the submit verb, and whether the assigned-data-bodies
// section and Delete action are shown.

function SourceModal({
  mode,
  detail,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  detail?: SourceDetail;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [form, setForm] = useState<SourceInput>(detail ? toInput(detail) : EMPTY_INPUT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = <K extends keyof SourceInput>(k: K, v: SourceInput[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  async function save() {
    if (!form.name.trim()) { setError('Company name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (mode === 'create') {await api.createSource(form);}
      else {await api.updateSource(detail!.id, form);}
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    setError('');
    try {
      await api.deleteSource(detail!.id);
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete');
      setSaving(false);
    }
  }

  const bodies = detail?.dataBodies ?? [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680, width: '100%' }}>
        <h2 className="modal-title">{mode === 'create' ? 'Add Source' : detail!.name}</h2>

        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
          <div className="form-group">
            <label className="form-label">Company</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Contact</label>
              <input className="form-input" value={form.contactName ?? ''} onChange={e => set('contactName', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.contactPhone ?? ''} onChange={e => set('contactPhone', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.contactEmail ?? ''} onChange={e => set('contactEmail', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input
              className="form-input"
              placeholder="Street address"
              value={form.addressLine1 ?? ''}
              onChange={e => set('addressLine1', e.target.value)}
            />
          </div>
          <div className="form-group">
            <input
              className="form-input"
              placeholder="Suite, floor, etc. (optional)"
              value={form.addressLine2 ?? ''}
              onChange={e => set('addressLine2', e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">City</label>
              <input className="form-input" value={form.city ?? ''} onChange={e => set('city', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">State</label>
              <input className="form-input" value={form.state ?? ''} onChange={e => set('state', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Postal Code</label>
              <input className="form-input" value={form.postalCode ?? ''} onChange={e => set('postalCode', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Country</label>
              <input className="form-input" value={form.country ?? ''} onChange={e => set('country', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              rows={4}
              placeholder="Terms, payout, contract dates, quirks in their payload, anything worth remembering."
              value={form.notes ?? ''}
              onChange={e => set('notes', e.target.value)}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {mode === 'edit' && (
            <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Data bodies ({bodies.length})
              </div>
              {bodies.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No data bodies assigned to this source yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {bodies.map(b => (
                    <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                      <Link to={`/body/${b.id}`} style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>{b.name}</Link>
                      <span style={{ color: '#9ca3af' }}>{b.status}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <Link
                  to={`/reports/sources/${detail!.id}`}
                  style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                >
                  View reporting for this source →
                </Link>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginTop: 12 }}>
            {error}
          </div>
        )}

        {confirmDelete && (
          <div style={{ padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 13, marginTop: 12 }}>
            Delete “{detail!.name}”? Its {bodies.length} data {bodies.length === 1 ? 'body' : 'bodies'} will stay,
            but become unassigned. Past reporting for them moves to “Unassigned”.
          </div>
        )}

        <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
          <div>
            {mode === 'edit' && !confirmDelete && (
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(true)} disabled={saving}>Delete</button>
            )}
            {mode === 'edit' && confirmDelete && (
              <button className="btn btn-secondary" onClick={remove} disabled={saving} style={{ color: '#b91c1c' }}>
                {saving ? 'Deleting…' : 'Yes, delete'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Add Source' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
